/**
 * react-router-backed replacements for the `next/navigation` and `next/link`
 * APIs used across the app. Kept API-compatible with Next so the migration only
 * had to swap the import source, not every call site.
 *
 * Prefer importing `useNavigate`, `useLocation`, `Link`, `Navigate` from
 * `react-router` directly in NEW code.
 */
import { useMemo } from "react";
import {
  useNavigate,
  useLocation,
  useParams as useParamsRR,
  useSearchParams as useSearchParamsRR,
  Link as RRLink,
  type LinkProps,
} from "react-router";

// Mirrors Next's `router` object. push/replace/back are the only methods used;
// refresh/forward/prefetch are no-ops (nothing to revalidate in a pure SPA).
// Memoized on `navigate` so the object is referentially stable across renders,
// like Next's router — effects that list `router` in their deps (e.g. the draft
// resume fetch in NewApplication) otherwise re-run on every render.
export function useRouter() {
  const navigate = useNavigate();
  // Second arg mirrors Next's NavigateOptions ({ scroll?, ... }); accepted for
  // call-site compatibility and ignored (SPA navigation needs no options here).
  return useMemo(
    () => ({
      push: (href: string, _options?: unknown) => navigate(href),
      replace: (href: string, _options?: unknown) => navigate(href, { replace: true }),
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => {},
      prefetch: (_href?: string) => {},
    }),
    [navigate],
  );
}

// Next's usePathname() → the current pathname string.
export function usePathname(): string {
  return useLocation().pathname;
}

// Next's useSearchParams() returns just the (read-only) URLSearchParams; react
// -router returns a [params, setter] tuple, so unwrap the first element.
export function useSearchParams(): URLSearchParams {
  const [searchParams] = useSearchParamsRR();
  return searchParams;
}

// Shape-compatible with Next's useParams().
export const useParams = useParamsRR;

// Compat for `next/link`'s default export: accepts Next's `href` (mapped to
// react-router's `to`) as well as `to`.
type CompatLinkProps = Omit<LinkProps, "to"> & {
  href?: LinkProps["to"];
  to?: LinkProps["to"];
};
export default function Link({ href, to, ...rest }: CompatLinkProps) {
  return <RRLink to={(to ?? href)!} {...rest} />;
}
